// medplum-patient-body.js — the ONE FHIR Patient body builder for the two
// browser-direct "make a new patient" doors.
// Consolidated 2026-08-05 (replatform W2a) — this builder was copy-pasted in
// medplum-patient-create.js (the outbox walk-in / quick-add path) and
// medplum-booking.js (the atomic booking transaction Bundle), the second
// carrying a "kept BYTE-FOR-BYTE in lock-step" comment because the first did
// not export it. A comment is not a guarantee; one shared function is. Diffed
// before merging: the two bodies were behaviourally identical (same field
// mapping, same identifier systems, same 0–130 age clamp) and differed only in
// comments and in naming `SLUG_SYSTEMS.PATIENT` via a local const. The parity
// test that guarded the copies — lib/__tests__/medplum-booking.test.js
// "buildPatientBody — parity with medplum-patient-create" — is RETAINED and now
// guards that the shared builder still emits what both callers expect.
//
// Field mapping stays in lock-step with updatePatientInMedplum
// (medplum-writes.js), so a patient created by either door round-trips through
// the reception edit dialog and every reader (lib/fhir-person.js
// nameOf/ageFromBirthDate) unchanged.
import { SLUG_SYSTEMS } from '@healther/outbox/outbox-ops.js';

// Phone identifier system — the human-facing phone business key. We STILL stamp
// this (so a phone lookup / the kiosk Bot's phone search finds the patient), but
// we DO NOT conditional-create on phone alone — the composite patient slug below
// is the idempotency key.
const PHONE_IDENTIFIER_SYSTEM = 'https://healther.io/opd/phone';

// Patient-slug identifier system — the composite (phone+name) idempotency key we
// conditional-create on. Shared verbatim with the offline outbox (outbox-ops.js
// SLUG_SYSTEMS.PATIENT) AND the server mirror, so a browser create, an offline
// replay, and a server-side mirror all converge on ONE Patient — no twins across
// any boundary.
const PATIENT_SLUG_SYSTEM = SLUG_SYSTEMS.PATIENT;

// Last-10 of an Indian mobile (the identifier value we key on), so
// '+91 98765 43210' and '9876543210' resolve to the same Patient.
// Consolidated 2026-08-05 (W2a) — was copy-pasted in medplum-patient-create.js
// and medplum-booking.js.
export const digitsOf = (v) => String(v || '').replace(/\D+/g, '').slice(-10);

/**
 * Build the FHIR Patient body from the quick-add / booking form shape
 * { name, age, sex, phone, language }. `slug` (when present) is the composite
 * phone+name idempotency key — we stamp it as an identifier so the caller's
 * If-None-Exist / transaction ifNoneExist search matches it.
 */
export function buildPatientBody({ name, age, sex, phone, language, slug }) {
  const body = { resourceType: 'Patient' };

  const nm = String(name || '').trim();
  if (nm) body.name = [{ text: nm }];

  // telecom phone + a phone identifier (the human contact key, NOT the
  // conditional-create key). We stamp BOTH the phone identifier AND — when we
  // have enough to be safe — the composite patient-slug identifier that the
  // caller's conditional create actually keys on.
  const phoneStr = String(phone || '').trim();
  const phoneKey = digitsOf(phoneStr);
  if (phoneStr) body.telecom = [{ system: 'phone', value: phoneStr }];
  const identifiers = [];
  if (phoneKey.length >= 10) identifiers.push({ system: PHONE_IDENTIFIER_SYSTEM, value: phoneKey });
  if (slug) identifiers.push({ system: PATIENT_SLUG_SYSTEM, value: slug });
  if (identifiers.length) body.identifier = identifiers;

  // sex F/M → gender (same mapping as updatePatientInMedplum).
  if (sex === 'F') body.gender = 'female';
  else if (sex === 'M') body.gender = 'male';

  // age → birthDate year precision (the disk model stored a bare age; every
  // reader derives age from birthDate — ageFromBirthDate in fhir-person.js).
  // Minting Jan-1 keeps the round-trip exact: a January-1 birthday has always
  // passed by the time anyone reads the row back.
  if (age !== undefined && age !== null && String(age).trim() !== '') {
    const n = Number(age);
    if (Number.isFinite(n) && n >= 0 && n <= 130) {
      const year = new Date().getUTCFullYear() - Math.round(n);
      body.birthDate = String(year) + '-01-01';
    }
  }

  // language → communication[0].language (the FHIR-correct home for it).
  const lang = String(language || '').trim();
  if (lang) {
    body.communication = [
      { language: { coding: [{ system: 'urn:ietf:bcp:47', code: lang }], text: lang }, preferred: true },
    ];
  }

  return body;
}
